import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, PackagePlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

const num = (v) => (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

export function SoAddStockDialog({ target, products, onOpenChange, onDone }) {
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("cases");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (target) { setQty(""); setUnit("cases"); setNote(""); setBusy(false); } }, [target]);

  const line = target?.line;
  const conv = useMemo(() => {
    if (!line) return null;
    const prod = (products || []).find((p) => p.id === line.product_id);
    const cfg = (prod?.pack_configs || []).find((c) => c.id === line.config_id) || (prod?.pack_configs || [])[0] || {};
    const casePack = line.case_pack || cfg.bottles_per_case || 0;
    const casesPerPallet = line.cases_per_pallet || cfg.cases_per_pallet || 0;
    const palletsPerTruck = cfg.pallets_per_truckload || 0;
    return { casePack, casesPerPallet, palletsPerTruck };
  }, [line, products]);

  if (!target || !conv) return null;

  const q = parseFloat(qty) || 0;
  const toCases = (v, u) => {
    if (u === "bottles") return conv.casePack ? v / conv.casePack : 0;
    if (u === "pallets") return v * (conv.casesPerPallet || 0);
    if (u === "truckloads") return v * (conv.casesPerPallet || 0) * (conv.palletsPerTruck || 0);
    return v;
  };
  const cases = toCases(q, unit);
  const bottles = cases * conv.casePack;
  const pallets = conv.casesPerPallet ? cases / conv.casesPerPallet : 0;

  const units = [["bottles", "Bottles"], ["cases", "Cases"]];
  if (conv.casesPerPallet) units.push(["pallets", "Pallets"]);
  if (conv.casesPerPallet && conv.palletsPerTruck) units.push(["truckloads", "Truckloads"]);

  const confirm = async () => {
    if (cases <= 0) { toast.error("Enter a quantity to add"); return; }
    setBusy(true);
    try {
      await api.post(`/sales-orders/${target.so.id}/add-stock`, {
        line_id: line.id, cases: Math.round(cases * 100) / 100,
        entered_qty: q, entered_unit: unit, note,
      });
      toast.success(`${num(cases)} cases added to finished goods — no components consumed`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to add stock");
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm" data-testid="so-add-stock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><PackagePlus className="h-5 w-5 text-emerald-600" /> Add Finished Goods to Stock</DialogTitle>
          <DialogDescription>
            Inventory-only addition — no components are consumed and no production is recorded.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
            <p className="font-semibold text-slate-800">{line.product_name}{line.design_name ? ` — ${line.design_name}` : ""}{conv.casePack ? ` — ${Math.round(conv.casePack)}-Pack` : ""}</p>
            <p className="mt-0.5 text-slate-500">For {target.so.so_number} · ordered {num(line.cases)} cases</p>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Quantity to Add</p>
              <Input value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0"
                className="h-9 rounded-sm tabular-nums" autoFocus data-testid="so-add-stock-qty" />
            </div>
            <div className="w-[130px]">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Unit</p>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="so-add-stock-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{units.map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>
          {q > 0 && (
            <p className="text-xs tabular-nums text-slate-600" data-testid="so-add-stock-equiv">
              = <span className="font-semibold">{num(bottles)}</span> bottles · <span className="font-semibold">{num(cases)}</span> cases{conv.casesPerPallet ? <> · <span className="font-semibold">{num(pallets)}</span> pallets</> : null}
            </p>
          )}
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Note / reason (optional)</p>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. found in warehouse, returned load" className="h-9 rounded-sm" data-testid="so-add-stock-note" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={busy} data-testid="so-add-stock-cancel">Cancel</Button>
          <Button className="rounded-sm bg-emerald-600 hover:bg-emerald-700" onClick={confirm} disabled={busy || cases <= 0} data-testid="so-add-stock-confirm">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Add to Stock
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
